import React from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Shield, TrendingUp, CheckCircle, Globe, BookOpen, Clock, Wallet } from 'lucide-react';

const VocationalPrograms = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950">
            <Navbar />
            <main className="pt-24 pb-16">
                {/* Hero Section */}
                <section className="py-20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 blur-3xl" />
                    <div className="container mx-auto px-4 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
                                <GraduationCap className="w-5 h-5 text-purple-400" />
                                <span className="text-purple-300 font-medium text-sm">Study & Work in Germany</span>
                            </div>
                            <h1 className="text-5xl md:text-7xl font-black text-white mb-6">
                                Dual Ausbildung <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-fuchsia-500">Programs</span>
                            </h1>
                            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                                Germany offers more than 350 professions (Ausbildungsberufe) where you can work and study at the same time. Earn a monthly salary while receiving a world-class "Made in Germany" qualification.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all"
                            >
                                Start Your Journey
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Program Details */}
                <section className="py-16">
                    <div className="container mx-auto px-4">
                        <div className="max-w-5xl mx-auto">
                            <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                                What is Duale Ausbildung?
                            </h2>
                            <div className="grid md:grid-cols-2 gap-8 mb-12">
                                <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-8">
                                    <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                        <BookOpen className="w-6 h-6 text-purple-400" /> The Dual System
                                    </h3>
                                    <p className="text-slate-300 mb-4 leading-relaxed">
                                        Participants of the dual educational system (Duale Ausbildung) work and study at the same time. This combination of theory and practice allows you to receive a recognized qualification without attending a traditional university.
                                    </p>
                                    <ul className="space-y-3">
                                        <li className="flex items-start gap-3 text-slate-300">
                                            <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 shrink-0" />
                                            <span>Practical training at an employing company (Ausbildungsbetrieb)</span>
                                        </li>
                                        <li className="flex items-start gap-3 text-slate-300">
                                            <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2 shrink-0" />
                                            <span>Theoretical study at a vocational training school (Ausbildungszentrum)</span>
                                        </li>
                                    </ul>
                                </div>
                                <div className="space-y-6">
                                    <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-6 flex items-center gap-6">
                                        <div className="w-16 h-16 shrink-0 bg-purple-500/20 rounded-2xl flex items-center justify-center text-purple-400">
                                            <Wallet className="w-8 h-8" />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-bold text-white mb-1">Earn While You Learn</h4>
                                            <p className="text-slate-400">You receive a monthly training salary (Ausbildungsvergütung) throughout the entire program.</p>
                                        </div>
                                    </div>
                                    <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-6 flex items-center gap-6">
                                        <div className="w-16 h-16 shrink-0 bg-purple-500/20 rounded-2xl flex items-center justify-center text-purple-400">
                                            <Clock className="w-8 h-8" />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-bold text-white mb-1">Duration</h4>
                                            <p className="text-slate-400">Programs generally last for 2 to 3.5 years, depending on your field and performance.</p>
                                        </div>
                                    </div>
                                    <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-6 flex items-center gap-6">
                                        <div className="w-16 h-16 shrink-0 bg-purple-500/20 rounded-2xl flex items-center justify-center text-purple-400">
                                            <TrendingUp className="w-8 h-8" />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-bold text-white mb-1">Career Prospects</h4>
                                            <p className="text-slate-400">Great job opportunities as vocational professions in Germany and across Europe after graduation.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Requirements */}
                <section className="py-16 bg-slate-800/30">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                            Eligibility & Requirements
                        </h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-purple-500/50 transition-all">
                                <div className="text-4xl mb-4">🎓</div>
                                <h3 className="text-xl font-bold text-white mb-3">Education</h3>
                                <p className="text-slate-400 text-sm">
                                    Passed high school (Class 12 or equivalent) in any subject stream. No specific background required for most trades.
                                </p>
                            </div>
                            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-purple-500/50 transition-all">
                                <div className="text-4xl mb-4">🗣</div>
                                <h3 className="text-xl font-bold text-white mb-3">German Language</h3>
                                <p className="text-slate-400 text-sm">
                                    Proficiency is a must. Usually B1 is required for technical trades, and B2 is mandatory for medical/nursing fields.
                                </p>
                            </div>
                            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-purple-500/50 transition-all">
                                <div className="text-4xl mb-4">🎂</div>
                                <h3 className="text-xl font-bold text-white mb-3">Age Factor</h3>
                                <p className="text-slate-400 text-sm">
                                    Applicants should generally be between 18 and 30 years old to qualify for the student/trainee visa.
                                </p>
                            </div>
                            <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-purple-500/50 transition-all">
                                <div className="text-4xl mb-4">📄</div>
                                <h3 className="text-xl font-bold text-white mb-3">Documents</h3>
                                <p className="text-slate-400 text-sm">
                                    Valid passport, motivation letter, CV, and translated/attested educational certificates are essential.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Popular Fields */}
                <section className="py-16">
                    <div className="container mx-auto px-4 text-center">
                        <h2 className="text-3xl font-black text-white mb-10">Popular Ausbildung Fields</h2>
                        <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
                            {['Nursing & Healthcare', 'IT & Software Development', 'Automotive Technology', 'Hospitality & Gastronomy', 'Retail & Sales', 'Electrical Engineering'].map((field, i) => (
                                <span key={i} className="px-6 py-3 bg-purple-500/10 border border-purple-500/30 text-purple-300 rounded-full font-medium">
                                    {field}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-16">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 border border-purple-500/30 rounded-3xl p-12">
                            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                                Ready to Secure Your Future in Germany?
                            </h2>
                            <p className="text-slate-300 mb-8">
                                We help with German language training, interviews with German employers, managing the training contract (Ausbildungsvertrag), housing, and your visa application.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] transition-all"
                            >
                                Apply Now
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default VocationalPrograms;
